import { createContext, useContext, useMemo } from "react";
import type { LeadFilterSectionId } from "../config/leadFilterIndustryConfig";

/**
 * Maps each section ID to the filter keys that live inside it.
 * Used to determine which sections have active filters.
 *
 * `nicheFilters` is shared between Academic Background and the industry
 * Preferences section. To distinguish the two we additionally inspect the
 * keys *inside* `nicheFilters` (see `nicheFilterBucketsForSection`).
 */
const SECTION_FILTER_KEYS: Record<LeadFilterSectionId, string[]> = {
  dates: [
    "startDate",
    "endDate",
    "startTime",
    "endTime",
    "dateType",
    // Armed from the Stage prompt too, but it only means anything under
    // "Status Changed At" — which is a Date Filters concern.
    "stageChangedTo",
    "entryLevel",
    "entryMovedOn",
  ],
  sourceCampaign: [
    "source",
    "sourceType",
    "metaPageId",
    "metaAdAccountId",
    "campaignId",
    // the campaign report ↗'s grain marker — cleared with its section
    "campaignGrain",
    // Meta ad set / ad — the section's own "Ad set" / "Ad" pickers (also set
    // by the campaign report's ↗)
    "adsetId",
    "adId",
  ],
  basic: [
    "branchId",
    "noBranch",
    "priority",
    "phoneCountryCode",
    "user",
    "assignmentStatus",
    "role",
  ],
  // `stageGrain` = a report drill ↗'s stage-match marker — it changes how
  // these chips match, so it lights the section up with them.
  funnel: ["stage", "status", "subStatus", "stageGrain"],
  tracks: ["trackId", "subTrackId", "trackStatus", "hasActiveTrack", "trackStartDate", "trackEndDate"],
  activity: [
    "idleOpenFor",
    "idleTouches",
    "lastContactedStartDate",
    "lastContactedEndDate",
    "contactedBy",
    "nextFollowupDate",
    "lastActivityDate",
    "activityType",
    "activityBy",
    "activityStart",
    "activityEnd",
  ],
  targets: ["targetLineId", "targetUserId", "targetAsOf"],
  calls: [
    "callActivity",
    "callDirection",
    "callOutcome",
    "callCategory",
    "callBy",
    "callStartDate",
    "callEndDate",
    "minCallDuration",
    "maxCallDuration",
    "minCallCount",
    "maxCallCount",
    "hasCallRecording",
  ],
  followup: [
    "followupStartDate",
    "followupEndDate",
    "trackFollowupStartDate",
    "trackFollowupEndDate",
    "trackFollowupTrackId",
  ],
  academic: ["nicheFilters"],
  // `intakeKeys` = the report bar's Intake pill riding a report ↗ — a study
  // preference like the two beside it, so it lights this section up.
  leadPreference: ["customFieldFilters", "customFieldPairs", "intakeKeys"],
  // `customFieldPairs` (Intake Year × Month) is edited from the industry
  // Preferences section, so an applied pair must light that section up too —
  // otherwise the accordion stays collapsed over an active filter.
  preference: ["nicheFilters", "customFieldPairs"],
  choice: [
    "hasChoices",
    "numberOfChoices",
    "choicesAppliedIn",
    "choicesLastUpdated",
  ],
  payment: [
    "paymentStatus",
    "hasDealValue",
    "hasPendingAmount",
    "hasInstallmentPlan",
    "hasOverdueInstallments",
    "instRules",
    "instDue",
    "minDealValue",
    "maxDealValue",
    "minPendingAmount",
    "maxPendingAmount",
  ],
};

/**
 * Predicate per section that owns part of `nicheFilters`. Lets us light up
 * "Academic Background" when only academic-prefixed keys are set, and
 * "Preferences" when only niche-id keys are set, even though they share the
 * same applied-filters bucket.
 */
const nicheFilterMatchers: Partial<
  Record<LeadFilterSectionId, (key: string) => boolean>
> = {
  academic: (k) =>
    k === "qualLevelId" ||
    k.startsWith("qualScoreMin_") ||
    k.startsWith("qualScoreMax_"),
  // Anything else inside nicheFilters belongs to the industry Preferences
  // section (universityId, courseId, videoServiceId, etc.).
  preference: (k) =>
    !(
      k === "qualLevelId" ||
      k.startsWith("qualScoreMin_") ||
      k.startsWith("qualScoreMax_")
    ),
};

/** Human-readable labels for applied filter keys (used in tooltip) */
const FILTER_KEY_LABELS: Record<string, string> = {
  source: "Lead Source",
  sourceType: "Source Type",
  metaPageId: "Meta Page",
  metaAdAccountId: "Meta Ad Account",
  campaignId: "Campaign",
  adsetId: "Ad set",
  adId: "Ad",
  user: "Assigned User",
  role: "Role",
  assignmentStatus: "Assignment Status",
  branchId: "Branch",
  noBranch: "No Branch",
  priority: "Priority",
  phoneCountryCode: "Country Code",
  stage: "Stage",
  status: "Status",
  subStatus: "Sub Status",
  startDate: "Start Date",
  startTime: "From Time",
  endTime: "To Time",
  endDate: "End Date",
  dateType: "Date Type",
  stageChangedTo: "Changed Into Stage",
  entryLevel: "Change Counted",
  entryMovedOn: "Entry Counts",
  inactiveFor: "Inactive For",
  idleOpenFor: "Paused Activity For",
  idleTouches: "Counted Activity",
  lastContactedStartDate: "Last Contacted From",
  lastContactedEndDate: "Last Contacted To",
  contactedBy: "Contacted By",
  nextFollowupDate: "Next Follow-up",
  followupStartDate: "Follow-up From",
  followupEndDate: "Follow-up To",
  trackFollowupStartDate: "Track follow-up From",
  trackFollowupEndDate: "Track follow-up To",
  trackFollowupTrackId: "Track follow-up · Track",
  lastActivityDate: "Last Activity",
  activityType: "Team Activity",
  activityBy: "Activity By",
  activityStart: "Activity From",
  activityEnd: "Activity To",
  callActivity: "Call Activity",
  callDirection: "Call Direction",
  callOutcome: "Call Outcome",
  callCategory: "Call Source",
  callBy: "Called By",
  targetLineId: "Target",
  targetUserId: "Target · Whose",
  targetAsOf: "Target · As of",
  callStartDate: "Called From",
  callEndDate: "Called To",
  minCallDuration: "Min Talk Time",
  maxCallDuration: "Max Talk Time",
  minCallCount: "Min Calls",
  maxCallCount: "Max Calls",
  hasCallRecording: "Has Call Recording",
  trackId: "Track",
  subTrackId: "Sub Track",
  trackStatus: "Track Status",
  trackStartDate: "Track started from",
  trackEndDate: "Track started to",
  hasActiveTrack: "Has Active Track",
  hasChoices: "Has Deals",
  numberOfChoices: "Number of Deals",
  choicesAppliedIn: "Deals Applied In",
  choicesLastUpdated: "Deals Last Updated",
  nicheFilters: "Preferences",
  customFieldFilters: "Custom Fields",
  customFieldPairs: "Intake",
  isRevoked: "Revoked",
  isReenquired: "Re-enquired",
  isActive: "Active/Inactive",
  paymentStatus: "Payment Status",
  hasDealValue: "Has Deal Value",
  hasPendingAmount: "Has Pending Amount",
  hasInstallmentPlan: "Has EMI Plan",
  hasOverdueInstallments: "Has Overdue Instalments",
  instRules: "Instalments",
  instDue: "Next Instalment Due",
  minDealValue: "Min Deal Value",
  maxDealValue: "Max Deal Value",
  minPendingAmount: "Min Pending Amount",
  maxPendingAmount: "Max Pending Amount",
};

interface AppliedFiltersInfo {
  activeSections: Set<LeadFilterSectionId>;
  appliedCount: number;
  appliedLabels: string[];
  /** Every applied filter key (`customFieldPairs`, `intakeKeys`, …). */
  appliedKeys: ReadonlySet<string>;
  /** Custom fields with an applied value (`customFieldFilters` ids). */
  appliedCustomFieldIds: ReadonlySet<string>;
}

const AppliedFiltersContext = createContext<AppliedFiltersInfo>({
  activeSections: new Set(),
  appliedCount: 0,
  appliedLabels: [],
  appliedKeys: new Set(),
  appliedCustomFieldIds: new Set(),
});

export function useAppliedFiltersInfo(): AppliedFiltersInfo {
  return useContext(AppliedFiltersContext);
}

export function useIsSectionActive(sectionId: LeadFilterSectionId): boolean {
  const { activeSections } = useContext(AppliedFiltersContext);
  return activeSections.has(sectionId);
}

export function AppliedFiltersProvider({
  appliedFilters,
  children,
}: {
  appliedFilters: Record<string, any>;
  children: React.ReactNode;
}) {
  const info = useMemo(() => {
    const appliedKeys = Object.keys(appliedFilters);
    const activeSections = new Set<LeadFilterSectionId>();
    const appliedLabels: string[] = [];

    for (const key of appliedKeys) {
      const label = FILTER_KEY_LABELS[key];
      if (label) appliedLabels.push(label);

      for (const [sectionId, keys] of Object.entries(SECTION_FILTER_KEYS)) {
        if (!keys.includes(key)) continue;

        // For shared buckets (currently `nicheFilters`), only light up the
        // section if the inner keys actually belong to it.
        if (key === "nicheFilters") {
          const matcher = nicheFilterMatchers[sectionId as LeadFilterSectionId];
          if (matcher) {
            const inner = appliedFilters[key] as Record<string, unknown>;
            const innerKeys = inner ? Object.keys(inner) : [];
            const hasMatch = innerKeys.some((k) => matcher(k));
            if (!hasMatch) continue;
          }
        }
        activeSections.add(sectionId as LeadFilterSectionId);
      }
    }

    const cf = appliedFilters.customFieldFilters as Record<string, unknown> | undefined;
    const appliedCustomFieldIds = new Set(
      Object.entries(cf ?? {})
        .filter(([, v]) => Boolean(v))
        .map(([id]) => id)
    );

    return {
      activeSections,
      appliedCount: appliedKeys.length,
      appliedLabels,
      appliedKeys: new Set(appliedKeys),
      appliedCustomFieldIds,
    };
  }, [appliedFilters]);

  return (
    <AppliedFiltersContext.Provider value={info}>
      {children}
    </AppliedFiltersContext.Provider>
  );
}
